import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, X, Mail } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

/**
 * EmailGateForm
 * Modal that captures email/name/(optional) organisation, then requests a
 * magic-link download via POST /resources/request-download.
 *
 * Props:
 *  - open: boolean
 *  - resourceTitle: string
 *  - resourceId: string
 *  - onClose: () => void
 *  - onSuccess: (leadData) => void
 */
export default function EmailGateForm({
  open,
  resourceTitle,
  resourceId,
  onClose,
  onSuccess
}) {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setEmail('');
      setName('');
      setOrganisation('');
      setErrors({});
      setSubmitting(false);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  const validate = () => {
    const next = {};
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      next.email = 'Please enter a valid email address.';
    }
    if (!name || name.trim().length < 2) {
      next.name = 'Please enter your name.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;

    setSubmitting(true);
    try {
      const response = await fetch('/resources/request-download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim(),
          organisation: organisation.trim() || undefined,
          resourceId
        })
      });

      if (response.ok) {
        const leadData = {
          email: email.trim(),
          name: name.trim(),
          organisation: organisation.trim() || ''
        };
        toast({
          title: 'Check your inbox',
          description: 'Your download link is on its way. It will expire in 15 minutes.'
        });
        onSuccess?.(leadData);
        onClose?.();
        return;
      }

      // Try to parse error body
      let message = 'Something went wrong sending the link. Please try again or email contact@educ4te.com.';
      try {
        const data = await response.json();
        if (data?.error && typeof data.error === 'string') {
          message = data.error;
        } else if (data?.message && typeof data.message === 'string') {
          message = data.message;
        }
      } catch {
        // ignore body parse errors
      }

      if (response.status === 400 || response.status === 429) {
        toast({
          title: 'We couldn\'t send your link',
          description: message,
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Something went wrong',
          description: 'Something went wrong sending the link. Please try again or email contact@educ4te.com.',
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('[EmailGateForm] request-download error:', err);
      toast({
        title: 'Something went wrong',
        description: 'Something went wrong sending the link. Please try again or email contact@educ4te.com.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="email-gate-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !submitting && onClose?.()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-gate-title"
        >
          <motion.div
            key="email-gate-card"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#422680] via-[#660F56] to-[#F54952] px-6 py-5 text-white">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                aria-label="Close"
                className="absolute top-3 right-3 text-white/80 hover:text-white transition-colors disabled:opacity-40"
              >
                <X size={20} />
              </button>
              <div className="flex items-center gap-3">
                <div className="bg-white/15 rounded-lg p-2">
                  <Mail size={20} />
                </div>
                <h2 id="email-gate-title" className="text-lg md:text-xl font-bold leading-tight pr-6">
                  Get <span className="underline decoration-white/50 underline-offset-4">{resourceTitle}</span> delivered to your inbox
                </h2>
              </div>
            </div>

            {/* Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4" noValidate>
              <p className="text-sm text-gray-600">
                We'll email you a download link valid for 15 minutes. We may follow up about Copilot governance services.
              </p>

              <div>
                <label htmlFor="lead-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-[#F54952]">*</span>
                </label>
                <input
                  id="lead-email"
                  type="email"
                  required
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                  placeholder="you@organisation.com.au"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#422680] focus:border-transparent disabled:bg-gray-50 ${
                    errors.email ? 'border-[#F54952]' : 'border-gray-200'
                  }`}
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? 'lead-email-error' : undefined}
                />
                {errors.email && (
                  <p id="lead-email-error" className="mt-1 text-xs text-[#F54952]">{errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="lead-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-[#F54952]">*</span>
                </label>
                <input
                  id="lead-name"
                  type="text"
                  required
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={submitting}
                  placeholder="Full name"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#422680] focus:border-transparent disabled:bg-gray-50 ${
                    errors.name ? 'border-[#F54952]' : 'border-gray-200'
                  }`}
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={errors.name ? 'lead-name-error' : undefined}
                />
                {errors.name && (
                  <p id="lead-name-error" className="mt-1 text-xs text-[#F54952]">{errors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="lead-organisation" className="block text-sm font-medium text-gray-700 mb-1">
                  Organisation
                </label>
                <input
                  id="lead-organisation"
                  type="text"
                  autoComplete="organization"
                  value={organisation}
                  onChange={(e) => setOrganisation(e.target.value)}
                  disabled={submitting}
                  placeholder="Organisation (optional)"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#422680] focus:border-transparent disabled:bg-gray-50"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-[#422680] via-[#660F56] to-[#F54952] text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sending link...
                  </>
                ) : (
                  <>
                    <Mail size={18} />
                    Send me the link
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 leading-relaxed">
                We'll email you a one-time download link valid for 15 minutes. We may follow up with relevant Copilot governance insights — you can unsubscribe any time.
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
